// Tiny react-query wrapper around `hrStatsApi.getBreakdown` used by the
// People Insights page. Each section (Geo / Org / Composition) needs its
// own dimension breakdown that is independent of the page-level `groupBy`
// state from `useHrStats`, so we expose a focused hook here.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { hrStatsApi } from '../services/api';
import type {
  HrDimension,
  HrEmployeeStatus,
  HrFilterMap,
  HrStatsBreakdownResponse,
} from '../types/hrStats';

export interface UsePeopleBreakdownArgs {
  dimension: HrDimension;
  filters: HrFilterMap;
  employeeStatus: HrEmployeeStatus;
  /** Set to `false` to defer the request (e.g. drill not yet opened). */
  enabled?: boolean;
  /** Optional react-query stale time override. */
  staleTimeMs?: number;
}

/**
 * Returns the breakdown rows for one dimension under the given filter set.
 * Multiple sections call this in parallel; react-query dedupes identical
 * payloads, so doing so is cheap.
 */
export function usePeopleBreakdown({
  dimension,
  filters,
  employeeStatus,
  enabled = true,
  staleTimeMs = 60 * 1000,
}: UsePeopleBreakdownArgs): UseQueryResult<HrStatsBreakdownResponse, unknown> {
  return useQuery({
    queryKey: ['people-insights', 'breakdown', dimension, filters, employeeStatus],
    queryFn: () =>
      hrStatsApi.getBreakdown({
        group_by: dimension,
        filters,
        employee_status: employeeStatus,
      }),
    enabled,
    staleTime: staleTimeMs,
  });
}
